import { useEffect, useRef, useState } from "react"
import maplibregl from "maplibre-gl"

import { useKMZParser } from "@/hooks/use-kmz-parser"

import { useMapLibre } from "./maplibre-context"

export function KMZLayerLibre({ kmzUrl, kmz }: { kmzUrl: string; kmz: any }) {
  const map = useMapLibre()
  const { loadStaticKMZ } = useKMZParser()
  const [geoJson, setGeoJson] = useState<any>(null)
  const addedRef = useRef(false) // Para maiwasan ang duplicate add

  // Load KMZ → local state
  useEffect(() => {
    if (!kmzUrl) return
    let cancelled = false
    loadStaticKMZ(kmzUrl).then((data) => {
      if (!cancelled && data) setGeoJson(data)
    })
    return () => {
      cancelled = true
    }
  }, [kmzUrl, loadStaticKMZ])

  // Function to add layers
  const addLayers = async () => {
    if (!map || !geoJson || addedRef.current) return

    // Hintaying mag‑load ang style
    if (!map.isStyleLoaded()) {
      await new Promise((resolve) => map.once("idle", resolve))
    }

    // Double‑check kung na‑add na (baka may nag‑interfere)
    if (addedRef.current) return

    const sourceId = `kmz-source-${kmz.id}`
    const fillId = `kmz-fill-${kmz.id}`
    const lineId = `kmz-line-${kmz.id}`
    const pointId = `kmz-point-${kmz.id}`

    try {
      // Source
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: "geojson",
          data: geoJson,
        })
      }

      // Fill (polygons)
      if (!map.getLayer(fillId)) {
        map.addLayer({
          id: fillId,
          type: "fill",
          source: sourceId,
          paint: {
            "fill-color": "#ff7800",
            "fill-opacity": 0.25,
          },
          filter: ["==", "$type", "Polygon"],
        })
      }

      // Line (linestrings)
      if (!map.getLayer(lineId)) {
        map.addLayer({
          id: lineId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#0096C7",
            "line-width": 4,
          },
          filter: [
            "any",
            ["==", ["geometry-type"], "LineString"],
            ["==", ["geometry-type"], "MultiLineString"],
          ],
        })
      }

      // Point (circles)
      if (!map.getLayer(pointId)) {
        map.addLayer({
          id: pointId,
          type: "circle",
          source: sourceId,
          paint: {
            "circle-radius": 6,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff",
          },
          filter: ["==", "$type", "Point"],
        })
      }

      addedRef.current = true
      console.log(`KMZ ${kmz.id} layers added`)
    } catch (err) {
      console.error(`Error adding KMZ ${kmz.id}:`, err)
    }
  }

  // Initial add + re‑add on style change
  useEffect(() => {
    if (!map || !geoJson) return

    // Add initially
    addLayers()

    // Listen for style load events (after setStyle)
    const onStyleLoad = () => {
      // Pag nagbago ang style, nawawala ang source. I-reset ang flag at subukang i‑add ulit.
      addedRef.current = false
      addLayers()
    }

    map.on("style.load", onStyleLoad)

    // Cleanup
    return () => {
      map.off("style.load", onStyleLoad)

      // Optional: alisin ang layers kapag na‑unmount ang component
      if (!map.getStyle()) return

      const sourceId = `kmz-source-${kmz.id}`
      const fillId = `kmz-fill-${kmz.id}`
      const lineId = `kmz-line-${kmz.id}`
      const pointId = `kmz-point-${kmz.id}`

      ;[fillId, lineId, pointId].forEach((layerId) => {
        if (map.getLayer(layerId)) map.removeLayer(layerId)
      })
      if (map.getSource(sourceId)) map.removeSource(sourceId)
      addedRef.current = false
    }
  }, [map, geoJson, kmz.id])

  return null
}
